import { ReactNode, useMemo, useState } from "react";
import { IconButton, Menu, MenuItem, TableCell, TableSortLabel, Tooltip, Checkbox, ListItemText } from "@mui/material";
import { FilterList as FilterListIcon } from "@mui/icons-material";
import { map, xor } from "lodash-es";
import { useTranslation } from "react-i18next";

/**
 * TableHeadCell component
 * acts as a head cell component with sorting and filtering functionality
 * sorting is required
 * filtering is optional
 */

interface Props<OrderColumn> {
  children: ReactNode;
  dataKey: OrderColumn;
  orderBy: OrderColumn;
  orderDirection: "asc" | "desc";
  tooltip: string;
  filters: Filter[];
  activeFilterIds: Array<string | number>;
  onSort: () => void;
  onFilter: (filterIds: Array<string | number>) => void;
}

const defaultProps = {
  filters: [],
  activeFilterIds: [],
  onFilter: () => {},
  tooltip: "",
};

type Filter = {
  id: number | string;
  label: string;
};

function TableHeadCell<OrderColumn>(props: Props<OrderColumn>) {
  const { t } = useTranslation();
  const { orderBy, orderDirection, filters, activeFilterIds: activeFilters } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isIndeterminate = useMemo(
    () => activeFilters.length !== 0 && filters.length !== activeFilters.length,
    [activeFilters.length, filters.length]
  );

  const isAllChecked = useMemo(() => filters.length === activeFilters.length, [activeFilters.length, filters.length]);

  /**
   * Functions
   */
  function checkAll() {
    if (!isAllChecked) {
      // toggle unchecked filters
      const unchecked = xor(map(filters, "id"), activeFilters);
      props.onFilter(unchecked);
    } else {
      // toggle all filters
      props.onFilter(map(filters, "id"));
    }
  }

  function handleFilter(filter: Filter) {
    props.onFilter([filter.id]);
  }

  return render();

  function render() {
    return (
      <TableCell>
        <TableSortLabel active={orderBy === props.dataKey} direction={orderDirection} onClick={() => props.onSort()}>
          {props.children}

          {props.filters.length > 0 && (
            <Tooltip title={props.tooltip || ""}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                }}
                size="large"
              >
                <FilterListIcon fontSize="small" color={isIndeterminate ? "secondary" : "disabled"} />
              </IconButton>
            </Tooltip>
          )}
        </TableSortLabel>

        {/* Filter menu when opened */}
        <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
          <MenuItem value="ALL" onClick={() => checkAll()}>
            <Checkbox indeterminate={isIndeterminate} checked={isAllChecked} />
            <ListItemText primary={t("label.selectAll")} />
          </MenuItem>
          {filters.map((filterItem) => {
            if (!filterItem) return null;
            return (
              <MenuItem key={filterItem.id} value={filterItem.id} onClick={() => handleFilter(filterItem)}>
                <Checkbox checked={activeFilters.indexOf(filterItem.id) > -1} />
                <ListItemText primary={<span dangerouslySetInnerHTML={{ __html: filterItem.label }} />} />
              </MenuItem>
            );
          })}
        </Menu>
      </TableCell>
    );
  }
}

TableHeadCell.defaultProps = defaultProps;
export { TableHeadCell };
